/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import AskButton from 'bundles/ai-coach-platform/components/patterns/popover/components/AskButton';
import QuickAccessDropdown from 'bundles/ai-coach-platform/components/patterns/popover/components/QuickAccessDropdown';
import SuggestedActionsToolbar from 'bundles/ai-coach-platform/components/patterns/popover/components/SuggestedActionsToolbar';
import { COACH_LABEL } from 'bundles/ai-coach-platform/components/patterns/popover/constants';
import { QuickAccessContext } from 'bundles/ai-coach-platform/components/patterns/popover/context/quickAccessContext';
import { usePopover } from 'bundles/ai-coach-platform/components/patterns/popover/hooks/usePopover';
import type { Action, Position } from 'bundles/ai-coach-platform/components/patterns/popover/types';

import _t from 'i18n!nls/ai-coach-platform';

type Props = {
  /**
   * Position of the dropdown (default is bottom-start)
   */
  position?: Position;

  /**
   * Quick actions to render in the dropdown (optional)
   */
  actions?: Action[];

  /**
   * Whether the button trigger is disabled
   */
  disabled?: boolean;
};

const styles = {
  cta: css`
    padding: var(--cds-spacing-100);
  `,
  dropdown: css`
    /* override default dropdown width */
    width: auto;
  `,
};

/**
 * The Suggested Actions Popover pattern consists of a CTA label that triggers a popover with action buttons.
 * Use this pattern when you need a quick way to trigger Coach or send Coach message from outside the chat UI. The actual
 * trigger logic would be implemented by the team using callbacks, this pattern only provides the UI pattern.
 */
const SuggestedActionsPopover = ({ actions, disabled, position = 'top-end' }: Props) => {
  const [open, setOpen] = useState(false);

  const handleClose = useCallback<() => void>(() => {
    setOpen(false);
  }, []);

  const context = usePopover({ open, position, onOpenChange: setOpen });

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <QuickAccessContext.Provider value={context}>
      <AskButton open={open} disabled={disabled} />

      <QuickAccessDropdown
        open={open}
        onClose={handleClose}
        additionalStyles={styles.dropdown}
        ariaLabel={_t('#{coachLabel} suggested actions', { coachLabel: COACH_LABEL })}
      >
        <SuggestedActionsToolbar actions={actions} onClose={handleClose} />
      </QuickAccessDropdown>
    </QuickAccessContext.Provider>
  );
};

export default SuggestedActionsPopover;
